import { useEffect, useState } from "react";
import axios from "axios";
import { AnimateIn } from "@/hooks/useAnimations";
import { Twitter, Instagram, Linkedin, Globe, UserCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SocialLink({ href, Icon, label }) {
  if (!href) return null;
  const url = href.startsWith("http") ? href : `https://${href}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="tap w-8 h-8 flex items-center justify-center border border-white/15 text-zinc-400 hover:text-white hover:border-red-600 hover:bg-red-600 transition-colors"
    >
      <Icon size={13} />
    </a>
  );
}

export default function TeamSection() {
  const [members, setMembers] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/team`)
      .then((r) => setMembers(r.data || []))
      .catch(() => setMembers([]))
      .finally(() => setLoaded(true));
  }, []);

  // Hide entire section if nobody has opted-in yet (avoids empty-state on the
  // landing page until Kamran toggles his own public_profile on).
  if (loaded && members.length === 0) return null;

  return (
    <section
      id="team"
      data-testid="team-section"
      className="py-14 sm:py-20 lg:py-24 bg-[#0A0A0A] relative scroll-mt-20 grid-lines"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <AnimateIn>
          <div className="text-center mb-10 sm:mb-16">
            <span className="text-4xl sm:text-6xl font-black text-red-600/10 block mb-1 sm:mb-2">
              03
            </span>
            <p className="section-label text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-red-600 justify-center">
              The Humans Behind The Code
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight">
              Meet The <span className="text-red-600">Team</span>
            </h2>
            <div className="section-divider" />
          </div>
        </AnimateIn>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {members.map((m, i) => {
            const avatar = m.avatar_url
              ? `${process.env.REACT_APP_BACKEND_URL}${m.avatar_url}`
              : null;
            return (
              <AnimateIn
                key={m.id}
                delay={0.18 * (i % 3)}
                direction={i % 2 === 0 ? "left" : "right"}
              >
                <div
                  data-testid={`team-member-${m.id}`}
                  className="card-accent card-top-bar group border border-white/10 bg-[#111111] p-5 sm:p-7 hover:border-red-600/70 transition-all duration-300 hover:-translate-y-2 hover:shadow-[8px_8px_0_0_rgba(229,9,20,0.85)] h-full"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 shrink-0 bg-[#1a1a1a] border border-white/10 overflow-hidden flex items-center justify-center">
                      {avatar ? (
                        <img
                          src={avatar}
                          alt={m.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <UserCircle2 size={28} className="text-zinc-600" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-black uppercase tracking-tight text-white truncate">
                        {m.name}
                      </h3>
                      <p className="text-[10px] text-red-500 uppercase tracking-[0.2em] font-bold truncate">
                        {m.title || (m.tags && m.tags[0]?.name) || "Team"}
                      </p>
                    </div>
                  </div>

                  {m.bio && (
                    <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed mb-4 line-clamp-4">
                      {m.bio}
                    </p>
                  )}

                  {m.tags && m.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {m.tags.slice(0, 4).map((t) => (
                        <span
                          key={t.id}
                          className="text-[9px] font-bold uppercase tracking-widest px-2 py-1 border"
                          style={{ color: t.color, borderColor: `${t.color}55` }}
                        >
                          {t.name}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2 mt-auto">
                    <SocialLink href={m.socials?.twitter} Icon={Twitter} label="Twitter" />
                    <SocialLink href={m.socials?.instagram} Icon={Instagram} label="Instagram" />
                    <SocialLink href={m.socials?.linkedin} Icon={Linkedin} label="LinkedIn" />
                    <SocialLink href={m.socials?.website} Icon={Globe} label="Website" />
                  </div>
                </div>
              </AnimateIn>
            );
          })}
        </div>
      </div>
    </section>
  );
}
